import React, { useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { FiUpload } from "react-icons/fi";
import Select from "../../../../../components/shared/Select/Select";
import Button from "../../../../../components/shared/Button/Button";
import Divider from "../../../../../components/shared/Divider/Divider";
import Checkbox from "../../../../../components/shared/Checkbox/Checkbox";
import { useNewton } from "../store/NewtonContext";
import Card from "../../../../../components/shared/Card/Card";
import { getHeightOptions } from "../../../../../components/shared/utils/heightOptions";

const VideoMetadata = () => {
  const { setIsMetadataOpen, setVideoMetadata, videoFile, videoMetadata } =
    useNewton();

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      club: videoMetadata.club,
      fps: videoMetadata.fps,
      gender: videoMetadata.gender,
      heightInInches: videoMetadata.heightInInches,
      swingAngle: videoMetadata.swingAngle,
      swingIsLeftHanded: videoMetadata.swingIsLeftHanded,
    },
  });

  const heightOptions = useMemo(
    () =>
      getHeightOptions().map(({ feet, inches, totalInches }) => ({
        text: `${feet}'${inches}"`,
        value: totalInches,
      })),
    []
  );

  const watchedValues = watch();

  const isFormValid = useMemo(() => {
    return (
      videoFile &&
      watchedValues.gender &&
      watchedValues.heightInInches &&
      watchedValues.club &&
      watchedValues.fps
    );
  }, [videoFile, watchedValues]);

  const handleFormSubmit = (data) => {
    // Update context with form data
    setVideoMetadata({
      ...videoMetadata,
      club: data.club,
      fps: data.fps,
      gender: data.gender,
      heightInInches: data.heightInInches,
      swingAngle: data.swingAngle,
      swingIsLeftHanded: data.swingIsLeftHanded,
    });

    setIsMetadataOpen(false);
  };

  return (
    <>
      {/* Header */}
      <div className="bg-grayTrue900">
        <div className="flex items-start justify-between px-4 py-5">
          <h2 className="text-gray200 text-2xl font-bold">Video Metadata</h2>
        </div>

        <Divider color="grayTrue800" type="horizontal" />
      </div>

      <div className="scrollbar-hide flex flex-1 flex-col justify-end overflow-y-auto px-4 py-6">
        <form
          className="flex size-full flex-1 flex-col justify-between gap-6"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <Card
            borderColor="grayTrue800"
            className="flex flex-col justify-between gap-3 p-4 sm:flex-row sm:items-center"
            color="grayTrue800"
            rounded="lg"
          >
            <div className="flex items-center gap-3">
              <div className="bg-grayTrue600 flex size-16 items-center justify-center rounded-lg">
                <FiUpload className="text-gray100 size-6" />
              </div>
              <span className="text-xl font-medium text-white">Video</span>
            </div>
            <div className="text-gray100 truncate text-sm">
              {videoFile ? videoFile.name : "No file selected"}
            </div>
          </Card>

          {/* Form Fields Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Gender Selection */}
            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold text-white">Gender</div>
              <Controller
                control={control}
                name="gender"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <div className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        color="primary600"
                        isChecked={field.value === "MALE"}
                        onChange={() => field.onChange("MALE")}
                      />
                      <span className="text-base text-white">Male</span>
                    </div>
                    <div className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        color="primary600"
                        isChecked={field.value === "FEMALE"}
                        onChange={() => field.onChange("FEMALE")}
                      />
                      <span className="text-base text-white">Female</span>
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Club Selection */}
            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold text-white">Club</div>
              <Controller
                control={control}
                name="club"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <div className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        color="primary600"
                        isChecked={field.value === "D"}
                        onChange={() => field.onChange("D")}
                      />
                      <span className="text-base text-white">Driver</span>
                    </div>
                    <div className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        color="primary600"
                        isChecked={field.value === "7I"}
                        onChange={() => field.onChange("7I")}
                      />
                      <span className="text-base text-white">Iron</span>
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Swing Angle Selection */}
            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold text-white">
                Swing Angle
              </div>
              <Controller
                control={control}
                name="swingAngle"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <div className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        color="primary600"
                        isChecked={field.value === "FaceOn"}
                        onChange={() => field.onChange("FaceOn")}
                      />
                      <span className="text-base text-white">Face On</span>
                    </div>
                    <div className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        color="primary600"
                        isChecked={field.value === "DownTheLine"}
                        onChange={() => field.onChange("DownTheLine")}
                      />
                      <span className="text-base text-white">
                        Down The Line
                      </span>
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Height Selection */}
            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold text-white">Height*</div>
              <Controller
                control={control}
                name="heightInInches"
                render={({ field }) => (
                  <Select
                    btnInitialColor="grayTrue700"
                    btnSelectedColor="grayTrue700"
                    buttonProps={{
                      className:
                        "text-primary200 flex flex-row gap-2 items-center justify-start w-full",
                      rounded: "lg",
                    }}
                    className="w-full"
                    onChange={field.onChange}
                    options={heightOptions}
                    placeholder="Select height"
                    value={field.value}
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold text-white">Dexterity</div>
              <Controller
                control={control}
                name="swingIsLeftHanded"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <div className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        color="primary600"
                        isChecked={!field.value}
                        onChange={() => field.onChange(!field.value)}
                      />
                      <span className="text-base text-white">Right Handed</span>
                    </div>
                    <div className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        color="primary600"
                        isChecked={field.value}
                        onChange={() => field.onChange(!field.value)}
                      />
                      <span className="text-base text-white">Left Handed</span>
                    </div>
                  </div>
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold text-white">FPS*</div>
              <Controller
                control={control}
                name="fps"
                render={({ field }) => (
                  <Select
                    btnInitialColor="grayTrue700"
                    btnSelectedColor="grayTrue700"
                    buttonProps={{
                      className:
                        "text-primary200 flex flex-row gap-2 items-center justify-start w-full",
                      rounded: "lg",
                    }}
                    className="w-full"
                    onChange={field.onChange}
                    options={[
                      { text: "120 fps", value: 120 },
                      { text: "60 fps", value: 60 },
                    ]}
                    placeholder="Select fps"
                    value={field.value}
                  />
                )}
              />
            </div>
          </div>

          <Button
            className="mt-6 w-full"
            color="primary500"
            isDisabled={!isFormValid}
            size="lg"
            type="submit"
          >
            Submit
          </Button>
        </form>
      </div>
    </>
  );
};

export default VideoMetadata;
